// Parser + matcher for the deliverable search box. Supports a small
// grammar layered over the previous plain-substring search:
//   - whitespace separates terms;
//   - "quoted phrase" is a single term (spaces preserved);
//   - -word / -"quoted phrase" is an exclusion;
//   - bare words are inclusions.
// All include terms must match (AND); no exclude term may match; an empty
// query matches everything. Matching is case-insensitive substring against
// the deliverable name and clauseReference.
//
// Edge cases are handled defensively so a half-typed query never throws and
// never silently drops the user's intent:
//   - an unterminated quote ("foo / -"foo) degrades to a literal term;
//   - a lone '-' is ignored;
//   - empty quotes ("" / -"") are ignored;
//   - repeated terms are deduped (idempotent);
//   - an escaped quote (\") inside a phrase is a literal quote.

import type { Deliverable } from "../../../../types";

export interface ParsedSearchQuery {
  include: string[];
  exclude: string[];
}

export function parseSearchQuery(raw: string): ParsedSearchQuery {
  const include: string[] = [];
  const exclude: string[] = [];
  const chars = raw;
  let i = 0;
  const n = chars.length;

  const pushTerm = (term: string, isExclude: boolean) => {
    const value = term.toLowerCase();
    if (value.length === 0) return; // empty quotes / lone dash residue
    const bucket = isExclude ? exclude : include;
    if (!bucket.includes(value)) bucket.push(value);
  };

  while (i < n) {
    // Skip leading whitespace between terms.
    if (/\s/u.test(chars[i])) {
      i++;
      continue;
    }

    let isExclude = false;
    if (chars[i] === "-") {
      isExclude = true;
      i++;
      // A '-' immediately followed by whitespace or end-of-input is a lone
      // dash — ignore it (no term, no exclusion).
      if (i >= n || /\s/u.test(chars[i])) continue;
    }

    if (chars[i] === '"') {
      // Quoted phrase: consume to the closing quote, honouring \" escapes.
      i++; // opening quote
      let term = "";
      let closed = false;
      while (i < n) {
        if (chars[i] === "\\" && chars[i + 1] === '"') {
          term += '"';
          i += 2;
          continue;
        }
        if (chars[i] === '"') {
          closed = true;
          i++;
          break;
        }
        term += chars[i];
        i++;
      }
      // Whether or not the quote was terminated, the captured text is the
      // term — an unterminated quote degrades to a literal rather than
      // breaking the search. `closed` is intentionally unused beyond this.
      void closed;
      pushTerm(term, isExclude);
      continue;
    }

    // Bare word: consume to the next whitespace.
    let term = "";
    while (i < n && !/\s/u.test(chars[i])) {
      term += chars[i];
      i++;
    }
    pushTerm(term, isExclude);
  }

  return { include, exclude };
}

function haystack(d: Deliverable): string {
  return `${d.name}\n${d.clauseReference ?? ""}`.toLowerCase();
}

export function matchesSearchQuery(
  d: Deliverable,
  parsed: ParsedSearchQuery,
): boolean {
  if (parsed.include.length === 0 && parsed.exclude.length === 0) return true;
  const hay = haystack(d);
  for (const term of parsed.exclude) {
    if (hay.includes(term)) return false;
  }
  for (const term of parsed.include) {
    if (!hay.includes(term)) return false;
  }
  return true;
}
